import { SimpleVehicleProps } from '../../../types/types'
import TertiaryButton from '../../../_shared/Buttons/TertiaryButton'
import ArrowRight from '../../../icons/ArrowRight'
import { useNavigate } from 'react-router-dom'
import { WarningTriangle } from '../../../icons'
import { formatDate } from '../../../helpers/formatDate'
import formatNumber from '../../../helpers/formatNumber'
import {
  formatLeaseTerm,
  formatNextInspectionDate,
  formatSimulatedMileage,
  formatStatus,
} from '../../../helpers/formatTableValues'
import { useQuery } from 'react-query'
import { fetchLatestServiceInvoice } from '../../../../../../../api/newApi'
import { useChosenCompany, useUser } from '../../../../../../../context/UserProvider'

type CarInformationProps = {
  car: SimpleVehicleProps
  isOpen: boolean
  setShowCarInformation: (show: boolean) => void
}

const CarInformation = ({ car, isOpen, setShowCarInformation }: CarInformationProps) => {
  const carNavigation = !!car.accountNo ? { id: car.accountNo, type: 'account' } : { id: car.budgetNo, type: 'budget' }

  const navigate = useNavigate()

  const { user } = useUser()
  const company = useChosenCompany()

  const { data: serviceInvoice, isLoading: serviceInvoiceIsLoading } = useQuery({
    queryKey: ['servicefakt', car?.registrationNumber],
    queryFn: async () => {
      if (!car || !company?.id || !user?.id) return null
      if (!car.accountNo && !car.budgetNo) return null

      return await fetchLatestServiceInvoice(company.id, car.accountNo || car.budgetNo, user.id)
    },
    enabled: !!car && (!!car.accountNo || !!car.budgetNo) && isOpen,
  })

  const serviceInvoiceStatus = !serviceInvoiceIsLoading
    ? serviceInvoice?.[0]
      ? formatDate(serviceInvoice[0].date)
      : '-'
    : 'laster...'

  const InfoRow = ({ label, value, isOverdue = false }) => (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
      <span className="text-gray-600 text-sm">{label}</span>
      <div className="flex items-center gap-1">
        <span className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>{value || '-'}</span>
        {isOverdue && <div className="w-2 h-2 bg-red-500 rounded-full" />}
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-4">
      <h2 className=" text-xl ">
        {car.make} - {car.model}
      </h2>
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <span className="bg-blue-100 border-old-primary text-old-primary border-[1px] py-1 px-4 rounded-sm">
          {car.registrationNumber ? car.registrationNumber : car?.budgetNo}
        </span>
        <span>{car.year}</span>
        {car.fuel && <span> - {car.fuel}</span>}
      </div>
      <div className="group transition-all">
        <TertiaryButton
          onClick={() => {
            navigate(`/cars/${carNavigation.id}?type=${carNavigation.type}`)
          }}
        >
          <span className="transition-all group-hover:translate-x-1 flex flex-row gap-2">
            <span className="transition-transform">Gå til bilkort</span>
            <ArrowRight />
          </span>
        </TertiaryButton>
      </div>

      {car?.deviations && (
        <div className="flex flex-col gap-2 p-3 bg-red-50 border-b border-red-100">
          {car.deviations.map((deviation, index) => (
            <div key={index} className="flex items-center gap-2 text-red-700">
              <WarningTriangle />
              <p className=" text-xs">{deviation.description}</p>
            </div>
          ))}
        </div>
      )}

      <div className="p-4">
        <div className="space-y-0">
          <InfoRow label="Sjåfør" value={car.driverName} />
          <InfoRow label="Status" value={formatStatus(car.status)} />
          <InfoRow label="Løpetid" value={formatLeaseTerm(car.actualDeliveryDate, car.plannedReturnDate)} />
          <InfoRow
            label="Avtaleslutt"
            value={car.plannedReturnDate ? (formatDate(car.plannedReturnDate) ?? '-') : '-'}
          />
          <InfoRow label="Maks km" value={car.contractMileage ? `${formatNumber(car.contractMileage)} km` : '-'} />
          <InfoRow label="Kmstand" value={car.mileage ? `${formatNumber(car.mileage)} km` : '-'} />
          <InfoRow label="KM-simulert" value={formatSimulatedMileage(car.simulatedMileage, car.contractMileage)} />
          <InfoRow label="EU-kontrollfrist" value={formatNextInspectionDate(car.nextInspectionDate)} />
          <InfoRow
            label="Kobling bestilling"
            value={
              car.replacementCar?.registrationNumber ||
              car.replacementCar?.accountNo ||
              car.replacementCar?.budgetNo ||
              '-'
            }
          />
          <InfoRow label="Samfaktura" value={car.consolidatedInvoiceCode} />
          <InfoRow label="Fakturagruppe" value={car.invoiceGroup} />
          <InfoRow label="Biltype" value={car.vehicleType} />
          <InfoRow label="Kontaktperson" value={car.mainContactName} />
          <InfoRow label="Budsjett nr." value={car.budgetNo} />
          <InfoRow label="Siste servicefakt." value={serviceInvoiceStatus} />
        </div>
      </div>
    </div>
  )
}

export default CarInformation
